import type { ComponentType } from 'react';
// Temporarily hidden — only used by the unimplemented "Compare" button below.
// import { toast } from 'sonner';
import { CircleCheck, Droplet, Droplets, Wrench } from 'lucide-react';
import { Badge, Button, Card, CardAction, CardContent, CardHeader } from 'shared-ui';

import { PanelCloseButton } from '@/components/shared/SlidingPanel';
import { formatStatValue, type StatFormat } from '@/utils/formatStat';
import type { MapSelection, SelectionStats } from '@/hooks/useSelectionStats';

interface StatRowConfig {
    key: keyof SelectionStats;
    label: string;
    icon: ComponentType<{ className?: string; strokeWidth?: number; 'aria-hidden'?: boolean }>;
    format: StatFormat;
}

// Order, labels and icons follow the Figma design. Both AOI and DMA cards share this list.
const STAT_ROWS: readonly StatRowConfig[] = [
    { key: 'leaksFound', label: 'Leaks Found', icon: Droplets, format: 'integer' },
    // Volume Identified renders potentialSaving — the same field, formula and label as the
    // Global Stats Bar (and the External Portal's "Potential Saving"), so the two can never
    // disagree for one scope.
    { key: 'potentialSaving', label: 'Volume Identified (Ml/D)', icon: Droplet, format: 'volume' },
    { key: 'suitableForNoDigCount', label: 'Suitable For Origin No Dig®', icon: Wrench, format: 'integer' },
    { key: 'noDigFixes', label: 'Fixed By Origin No Dig®', icon: CircleCheck, format: 'integer' },
] as const;

const BADGE_LABELS: Record<MapSelection['type'], string> = {
    aoi: 'Area of Interest',
    dma: 'DMA',
};

// Primary action label mirrors the badge: AOIs "View Area of Interest", DMAs "View DMA".
const PRIMARY_BUTTON_LABELS: Record<MapSelection['type'], string> = {
    aoi: 'View Area of Interest',
    dma: 'View DMA',
};

interface MapSelectionStatsCardProps {
    selection: MapSelection;
    stats: SelectionStats | null;
    isLoading: boolean;
    isError: boolean;
    onClose: () => void;
    onView: () => void;
    // Whether to show the primary "View …" action. Hidden once the map is zoomed in to the
    // selection's own semantic-level mid-stop (AOIs at Stop 4, DMAs at Stop 6), where flying to
    // the feature would only recentre. Defaults to true (owner decides based on the current zoom).
    showViewButton?: boolean;
}

function StatRow({ icon: Icon, label, value }: { icon: StatRowConfig['icon']; label: string; value: string }) {
    return (
        <div className="flex items-center gap-3">
            <span className="bg-background border-border flex size-10 shrink-0 items-center justify-center rounded-md border">
                <Icon className="text-origin-blue size-4" strokeWidth={2.25} aria-hidden={true} />
            </span>
            <span className="flex items-baseline gap-2">
                <span className="text-origin-blue text-lg leading-7 font-extrabold">{value}</span>
                <span className="text-muted-foreground text-sm leading-5 font-medium tracking-normal whitespace-nowrap">
                    {label}
                </span>
            </span>
        </div>
    );
}

function StatRowSkeleton() {
    return (
        <div className="flex items-center gap-3">
            <span className="bg-muted size-10 shrink-0 animate-pulse rounded-md" />
            <span className="bg-muted h-5 w-8 animate-pulse rounded" />
            <span className="bg-muted h-5 w-28 animate-pulse rounded" />
        </div>
    );
}

/**
 * Floating stats card for a clicked Area of Interest or DMA, pinned to the top-left corner
 * of the map (not anchored to the clicked feature) so it always opens in a consistent
 * position. The top/left gap mirrors the search button's top-right gap (`inset-y-4 right-4`
 * in MapControls); on mobile the card is offset downward so it clears the floating sidebar
 * trigger. Only one card is ever shown (single selection state in MapView). Styling follows
 * the Figma "Area Hover" design; the close button reuses the shared `PanelCloseButton` so it
 * matches the exclusive side panels.
 *
 * The primary "View …" action (`onView`) flies the map to the selected feature. The
 * "Compare" action is not implemented yet — it raises an info toast (same pattern as the
 * unimplemented map controls) until comparison behaviour is built.
 */
export function MapSelectionStatsCard({
    selection,
    stats,
    isLoading,
    isError,
    onClose,
    onView,
    showViewButton = true,
}: MapSelectionStatsCardProps) {
    return (
        // Pinned to the map's top-left corner. `pointer-events-none` on the wrapper + `pointer-events-auto`
        // on the card lets map drag continue in the gap around it, matching MapControls/SemanticZoomControl.
        // `top-20` clears the mobile floating sidebar trigger; `md:top-4` restores the tight gap on desktop.
        // Width lives on the wrapper: the card's own max-width would resolve against this
        // shrink-to-fit absolute wrapper and permanently clamp the card 2rem narrower than
        // intended. On the wrapper, the max-width guard resolves against the map container.
        <div className="pointer-events-none absolute top-20 left-4 z-10 w-85 max-w-[calc(100%-2rem)] md:top-4">
            <Card className="pointer-events-auto w-full gap-[18px] rounded-md py-5 shadow-lg">
                <CardHeader className="px-5">
                    {/* Title with the type badge left-aligned below it — mirrors the POI hover card.
                        `min-w-0` lets the 1fr header column constrain so the title truncates (…)
                        instead of growing and shoving the close button off the card edge. */}
                    <div className="min-w-0">
                        <h2
                            className="truncate text-xl leading-7 font-black tracking-normal uppercase"
                            title={selection.name}
                        >
                            {selection.name}
                        </h2>
                        <div className="mt-3">
                            <Badge variant="secondary" className="h-[22px] rounded-lg px-2.5">
                                {BADGE_LABELS[selection.type]}
                            </Badge>
                        </div>
                    </div>
                    <CardAction>
                        {/* Reuses the exclusive side-panel close button so the affordance is consistent. */}
                        <PanelCloseButton onClick={onClose} aria-label="Close stats card" />
                    </CardAction>
                </CardHeader>

                {/* Inset divider under the header — spans the full internal (p-5) width. */}
                <div className="border-border mx-5 border-t" />

                <CardContent className="flex flex-col gap-2 px-5">
                    {isError ? (
                        <p className="text-destructive text-xs">Unable to load stats — please try again.</p>
                    ) : isLoading ? (
                        STAT_ROWS.map((row) => <StatRowSkeleton key={row.key} />)
                    ) : (
                        STAT_ROWS.map((row) => (
                            <StatRow
                                key={row.key}
                                icon={row.icon}
                                label={row.label}
                                value={formatStatValue(stats?.[row.key], row.format)}
                            />
                        ))
                    )}
                </CardContent>

                {/* Divider + primary "View …" action (flies the map to the selection). Both are
                    hidden once the map is zoomed past the selection's mid-stop, where View is a no-op. */}
                {showViewButton && (
                    <>
                        <div className="border-border mx-5 border-t" />

                        <div className="flex flex-col gap-2 px-5">
                            <Button type="button" variant="default" className="h-10 w-full rounded-md" onClick={onView}>
                                {PRIMARY_BUTTON_LABELS[selection.type]}
                            </Button>
                            {/* Temporarily hidden — Compare not implemented yet.
                            <Button
                                type="button"
                                variant="outline"
                                className="h-10 w-full rounded-md"
                                onClick={() => toast.info('Compare is not implemented yet')}
                            >
                                Compare
                            </Button>
                            */}
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}
